import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { missionService, matrixService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getLocalizedContent } from '../utils/language';
import ReactMarkdown from 'react-markdown';
import { mostraDataHora } from '../utils/dates';

function SubmissionReview() {
  const { matrixId } = useParams();
  const { t, i18n } = useTranslation();
  const [matrix, setMatrix] = useState(null);
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLegend, setShowLegend] = useState(false);
  const [gradeForm, setGradeForm] = useState({ grade: '', feedback: '' });
  const [savingGrade, setSavingGrade] = useState(false);
  const { isInstructor } = useAuth();

  useEffect(() => {
    if (!isInstructor) {
      toast.error(t('submissions.accessDeniedInstructorRequired'));
      return;
    }
    fetchSubmissionDetails();
  }, [matrixId, isInstructor]);

  const fetchSubmissionDetails = async () => {
    try {
      // Fetch matrix details
      const matrixResponse = await matrixService.getMatrix(matrixId);
      const matrixData = matrixResponse.data;
      setMatrix(matrixData);
      setGradeForm({
        grade: matrixData.grade === null || matrixData.grade === undefined ? '' : String(matrixData.grade),
        feedback: matrixData.feedback || '',
      });

      // Fetch mission details
      const missionResponse = await missionService.getMission(matrixData.mission_id);
      setMission(missionResponse.data);
    } catch (error) {
      toast.error(t('submissionReview.errorLoadingSubmissionDetails'));
      console.error('Submission review error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmission = async () => {
    if (!window.confirm(t('submissionReview.confirmResetSubmission'))) {
      return;
    }

    try {
      await matrixService.resetMatrix(matrixId);
      toast.success(t('submissionReview.submissionResetSuccess'));
      fetchSubmissionDetails(); // Refresh data
    } catch (error) {
      toast.error(t('submissions.errorResetSubmission'));
    }
  };

  const handleSaveGrade = async () => {
    const nota = gradeForm.grade.trim();
    const comentari = gradeForm.feedback.trim();
    if (nota === '' && comentari === '') {
      toast.error(t('submissionReview.emptyGrade'));
      return;
    }
    // La coma decimal és el que escriu tothom aquí; el número no.
    const valor = nota === '' ? null : parseFloat(nota.replace(',', '.'));
    if (valor !== null && (Number.isNaN(valor) || valor < 0 || valor > 10)) {
      toast.error(t('submissionReview.invalidGrade'));
      return;
    }

    setSavingGrade(true);
    try {
      await matrixService.gradeMatrix(matrixId, { grade: valor, feedback: comentari || null });
      toast.success(t('submissionReview.gradeSaved'));
      fetchSubmissionDetails();
    } catch (error) {
      const detall = error.response?.data?.detail;
      toast.error(typeof detall === 'string' ? detall : t('submissionReview.errorSavingGrade'));
    } finally {
      setSavingGrade(false);
    }
  };

  const handleRemoveGrade = async () => {
    if (!window.confirm(t('submissionReview.confirmRemoveGrade'))) return;
    try {
      await matrixService.ungradeMatrix(matrixId);
      toast.success(t('submissionReview.gradeRemoved'));
      fetchSubmissionDetails();
    } catch (error) {
      toast.error(t('submissionReview.errorSavingGrade'));
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'in_progress': { class: 'badge-warning', text: t('submissions.inProgress') },
      'submitted': { class: 'badge-success', text: t('submissions.submitted') },
      'time_expired': { class: 'badge-danger', text: t('submissions.timeExpired') }
    };
    const statusInfo = statusMap[status] || { class: 'badge-secondary', text: status };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  // El servidor dona les hores en UTC i sense marca de fus: llegides amb
  // `new Date()` sortien dues hores endarrerides a l'estiu.
  const formatDateTime = (dateString) => mostraDataHora(dateString, t('common.notAvailable'));

  // Get merged hypotheses (mission + user hypotheses)
  const getAllHypotheses = () => {
    return matrix?.merged_hypotheses || mission?.hypotheses || [];
  };

  // NOTA (03/08/2026): aquí hi havia una còpia literal de la regla de puntuació
  // de l'ACH, idèntica a la que hi havia a Matrix.js. Ara la calcula el servidor
  // i, en una entrega tancada, arriba TAL COM ERA en lliurar: si algun dia es
  // refina el mètode, aquesta pantalla no ha de canviar la nota de les entregues
  // antigues sense que ningú se n'assabenti.

  const hypothesisKey = (hypothesis) =>
    hypothesis?.key || (hypothesis?.is_user_created ? `u${hypothesis.id}` : `h${hypothesis?.id}`);

  const findCell = (hypothesis, evidenceId) => {
    const clau = hypothesisKey(hypothesis);
    return matrix?.cells?.find(
      c => (c.hypothesis_key || `h${c.hypothesis_id}`) === clau && c.evidence_id === evidenceId
    );
  };

  const calculateHypothesisScore = (hypothesis) => {
    const entrada = (matrix?.scores || []).find(s => s.key === hypothesisKey(hypothesis));
    return entrada ? entrada.score : 0;
  };

  const getSelectedHypothesis = () => {
    const hypotheses = getAllHypotheses();
    // Amb most_likely_hypothesis_key: fins ara només s'exposava l'identificador
    // de les hipòtesis de missió, així que si l'alumne triava com a més probable
    // una hipòtesi seva, l'instructor no la veia enlloc.
    if (!matrix.most_likely_hypothesis_key || !hypotheses) return null;
    return hypotheses.find(h => hypothesisKey(h) === matrix.most_likely_hypothesis_key);
  };

  const getDiagnosticityRanking = () => {
    if (!matrix.diagnosticity_ranking || !mission.evidence) return [];
    return matrix.diagnosticity_ranking.map(evidenceId =>
      mission.evidence.find(e => e.id === evidenceId)
    ).filter(Boolean);
  };

  if (!isInstructor) {
    return (
      <div className="card">
        <h3>{t('submissions.accessDenied')}</h3>
        <p>{t('submissions.instructorOnlyAccess')}</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">{t('submissionReview.loadingSubmission')}</div>;
  }

  if (!matrix || !mission) {
    return (
      <div className="card">
        <h3>{t('submissionReview.submissionNotFound')}</h3>
        <p>{t('submissionReview.submissionNotFoundDescription')}</p>
      </div>
    );
  }

  const isSubmitted = matrix.status === 'submitted' || matrix.status === 'time_expired';
  // La hipòtesi més viable també la diu el servidor, amb la mateixa regla i
  // sobre la puntuació congelada de l'entrega.
  const bestKey = matrix.best_hypothesis_key;
  const selectedHypothesis = getSelectedHypothesis();
  const diagnosticityRanking = getDiagnosticityRanking();

  // Les hipòtesis d'aquesta pantalla surten de `matrix.merged_hypotheses`, i
  // aquell endpoint NO resol l'idioma: `/matrices/{id}` retorna les
  // descripcions tal com són a la base de dades, o sigui objectes `{ca, en}`.
  // Qui les resol és aquesta pantalla —la taula ja ho feia—, i per això es
  // resolen aquí, un sol cop, en comptes de dins del JSX.
  //
  // Escriure'n una en cru tomba la pantalla sencera: React no sap dibuixar un
  // objecte i llença (error #31). Passava a la fitxa de la hipòtesi triada, o
  // sigui **només a les entregues on l'alumne n'havia triat una**; les altres
  // es revisaven sense problema i per això va costar de veure.
  //
  // El `||` també demanava resoldre-les abans: un objecte buit és cert, de
  // manera que `short_description || description` es quedava sempre amb la
  // primera encara que no digués res.
  const hipotesiCurta = getLocalizedContent(selectedHypothesis?.short_description, i18n.language);
  const hipotesiLlarga = getLocalizedContent(selectedHypothesis?.description, i18n.language);

  return (
    <div className="submission-review-container">
      <div className="review-header">
        <div className="header-content">
          <div className="breadcrumb">
            <Link to="/missions" className="breadcrumb-link">{t('nav.missions')}</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to={`/missions/${mission.id}`} className="breadcrumb-link">{mission.title}</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to={`/missions/${mission.id}/submissions`} className="breadcrumb-link">{t('submissions.submissions')}</Link>
            <span className="breadcrumb-separator">/</span>
            <span>{t('submissions.review')}</span>
          </div>
          <h1>{t('submissionReview.submissionReview')}</h1>
        </div>

        <div className="review-actions">
          <button onClick={handleResetSubmission} className="btn btn-warning">
            {t('submissions.resetSubmission')}
          </button>
        </div>
      </div>

      <div className="submission-info-card">
        <div className="submission-meta">
          <div className="meta-group">
            <label>{t('submissions.student')}:</label>
            <span>{matrix.user?.username || t('submissions.unknownUser')}</span>
          </div>
          <div className="meta-group">
            <label>{t('users.status')}:</label>
            {getStatusBadge(matrix.status)}
          </div>
          <div className="meta-group">
            <label>{t('submissions.started')}:</label>
            <span>{formatDateTime(matrix.started_at)}</span>
          </div>
          {isSubmitted && (
            <div className="meta-group">
              <label>{t('submissions.submitted')}:</label>
              <span>{formatDateTime(matrix.submitted_at)}</span>
            </div>
          )}
          <div className="meta-group">
            <label>{t('submissions.lastActivity')}:</label>
            <span>{formatDateTime(matrix.last_saved_at)}</span>
          </div>
        </div>
      </div>

      {/* Correcció. Només té sentit sobre feina tancada: mentre l'exercici
          corre, l'alumne encara l'està fent. */}
      {isSubmitted && (
        <div className="card grading-card">
          <h2>{t('submissionReview.grading')}</h2>
          <p className="text-muted">{t('submissionReview.gradingHint')}</p>

          <div className="form-group" style={{ maxWidth: '160px' }}>
            <label>{t('submissionReview.grade')}</label>
            <input
              type="text"
              inputMode="decimal"
              value={gradeForm.grade}
              onChange={(e) => setGradeForm(prev => ({ ...prev, grade: e.target.value }))}
              placeholder="0 – 10"
            />
          </div>

          <div className="form-group">
            <label>{t('submissionReview.feedback')}</label>
            <textarea
              rows={5}
              value={gradeForm.feedback}
              onChange={(e) => setGradeForm(prev => ({ ...prev, feedback: e.target.value }))}
              placeholder={t('submissionReview.feedbackPlaceholder')}
            />
          </div>

          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={handleSaveGrade} className="btn btn-primary" disabled={savingGrade}>
              {savingGrade ? t('common.loading') : t('submissionReview.saveGrade')}
            </button>
            {matrix.graded_at && (
              <button onClick={handleRemoveGrade} className="btn btn-secondary">
                {t('submissionReview.removeGrade')}
              </button>
            )}
            {matrix.graded_at && (
              <span className="text-muted">
                {t('submissionReview.gradedBy', {
                  username: matrix.graded_by?.username || '',
                  date: formatDateTime(matrix.graded_at),
                })}
              </span>
            )}
          </div>
        </div>
      )}

      {/* L'enunciat, plegat (16/08/2026).
          Qui corregeix ja el coneix —l'ha escrit ell—, i el que ve a mirar és
          la matriu, la hipòtesi que va triar l'alumne, les evidències que va
          marcar com a diagnòstiques i la seva redacció. Amb el relat sencer
          sempre obert, tot això començava una pantalla i mitja més avall i
          calia desplaçar-s'hi a cada entrega. Es desplega quan cal comprovar
          què deia exactament una evidència, i llavors es queda obert mentre no
          es canviï de pàgina.

          Mateix `<details>` que a la pantalla de l'examen: funciona amb el
          teclat i no demana cap estat. Allà sí que en porta, perquè s'ha de
          plegar sol en començar la missió.

          L'enunciat s'escriu en Markdown i aquí sortia en cru, amb els
          asteriscos i els coixinets a la vista: qui corregeix llegia una cosa
          diferent de la que va llegir l'alumne. */}
      <details className="mission-briefing">
        <summary className="briefing-summary">{t('matrix.missionBriefing')}</summary>
        <div className="story-content">
          <ReactMarkdown>
            {getLocalizedContent(mission.story || mission.description, i18n.language)}
          </ReactMarkdown>
        </div>
      </details>

      {/* ACH Matrix Display */}
      <div className="ach-analysis-section">
        <div className="analysis-header">
          <h2>{t('matrix.achAnalysisMatrix')}</h2>
          <div className="legend-toggle">
            <button className="legend-btn" onClick={() => setShowLegend(!showLegend)}>
              {showLegend ? t('matrix.hideLegend') : t('matrix.showLegend')}
            </button>
          </div>
        </div>

        <div className="matrix-layout">
          <div className="matrix-main">
            <div className="table-container">
              <table className="matrix-table">
                <thead>
                  <tr>
                    <th className="evidence-header">{t('matrix.evidenceHypothesis')}</th>
                    {getAllHypotheses().map((h) => (
                      <th key={hypothesisKey(h)} className={`hypothesis-header ${h.is_user_created ? 'user-hypothesis' : ''}`}>
                        <div className="hypothesis-cell">
                          <span className="hypothesis-code">{h.code}</span>
                          {h.is_user_created && (
                            <span className="user-badge" title={t('matrix.ownHypothesis')}>*</span>
                          )}
                          <span className="hypothesis-desc">
                            {getLocalizedContent(h.short_description, i18n.language) || getLocalizedContent(h.description, i18n.language)}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mission.evidence?.map((evidence) => (
                    <tr key={evidence.id}>
                      <td className="evidence-cell">
                        <div className="evidence-content">
                          <strong className="evidence-code">{evidence.code}</strong>
                          <span className="evidence-desc">
                            {getLocalizedContent(evidence.short_description, i18n.language) || getLocalizedContent(evidence.description, i18n.language)}
                          </span>
                        </div>
                      </td>
                      {getAllHypotheses().map((hypothesis) => {
                        const cell = findCell(hypothesis, evidence.id);

                        return (
                          <td key={`${evidence.id}-${hypothesisKey(hypothesis)}`} className="matrix-cell">
                            <div className={`rating-display rating-${cell?.rating || 'null'}`}>
                              {cell?.rating || '—'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="score-row">
                    <td className="score-header">
                      <div className="score-label">
                        <strong>{t('matrix.hypothesisScore')}</strong>
                      </div>
                    </td>
                    {getAllHypotheses().map((hypothesis) => {
                      const score = calculateHypothesisScore(hypothesis);
                      const isBest = bestKey === hypothesisKey(hypothesis);

                      return (
                        <td key={`score-${hypothesisKey(hypothesis)}`} className={`score-cell ${isBest ? 'best-score' : ''}`}>
                          <div className="score-value">
                            <span className="score-number">{score}</span>
                            {isBest && <span className="best-indicator">{t('matrix.mostViable')}</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Student Analysis Summary */}
      <div className="analysis-summary-section">
        <h2>{t('submissionReview.studentAnalysisSummary')}</h2>

        <div className="summary-grid">
          <div className="summary-card">
            <h3>{t('submissionReview.selectedHypothesis')}</h3>
            {selectedHypothesis ? (
              <div className="hypothesis-selection">
                <div className="hypothesis-header">
                  <span className="hypothesis-code">{selectedHypothesis.code}</span>
                  <span className="hypothesis-title">{hipotesiCurta || hipotesiLlarga}</span>
                </div>
                {hipotesiLlarga && hipotesiCurta && (
                  <p className="hypothesis-full-desc">{hipotesiLlarga}</p>
                )}
                <div className="selection-analysis">
                  <p><strong>{t('submissionReview.matrixScore')}:</strong> {calculateHypothesisScore(selectedHypothesis)}</p>
                  {bestKey === hypothesisKey(selectedHypothesis) ? (
                    <span className="badge badge-success">{t('submissionReview.matchesBestScore')}</span>
                  ) : (
                    <span className="badge badge-warning">{t('submissionReview.differentFromBestScore')}</span>
                  )}
                </div>
              </div>
            ) : (
              <p className="no-selection">{t('submissionReview.noHypothesisSelected')}</p>
            )}
          </div>

          <div className="summary-card">
            <h3>{t('matrix.diagnosticityRanking')}</h3>
            {diagnosticityRanking.length > 0 ? (
              <div className="diagnosticity-ranking">
                {diagnosticityRanking.map((evidence, index) => (
                  <div key={evidence.id} className="ranked-evidence-item">
                    <div className="rank-number">{index + 1}</div>
                    <div className="evidence-info">
                      <span className="evidence-code">{evidence.code}</span>
                      <span className="evidence-desc">{getLocalizedContent(evidence.short_description, i18n.language) || getLocalizedContent(evidence.description, i18n.language)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-ranking">{t('submissionReview.noDiagnosticityRanking')}</p>
            )}
          </div>
        </div>

        <div className="assessment-card">
          <h3>{t('submissionReview.studentAssessment')}</h3>
          {matrix.conclusion ? (
            <div className="assessment-content">
              <div className="assessment-text">
                {matrix.conclusion.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              <div className="assessment-meta">
                <span className="char-count">{matrix.conclusion.length} {t('matrix.characters')}</span>
              </div>
            </div>
          ) : (
            <p className="no-assessment">{t('submissionReview.noAssessmentProvided')}</p>
          )}
        </div>
      </div>

      {/* Legend Modal */}
      {showLegend && (
        <div className="modal-overlay" onClick={() => setShowLegend(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>{t('matrix.ratingLegend')}</h4>
              <button className="modal-close" onClick={() => setShowLegend(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="legend-items">
                <div className="legend-item">
                  <span className="legend-badge rating-CC">CC</span>
                  <span>{t('matrix.veryConsistent')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge rating-C">C</span>
                  <span>{t('matrix.consistent')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge rating-N">N</span>
                  <span>{t('matrix.neutral')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge rating-I">I</span>
                  <span>{t('matrix.inconsistent')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge rating-II">II</span>
                  <span>{t('matrix.veryInconsistent')}</span>
                </div>
              </div>

              <div className="scoring-info">
                <h5>{t('matrix.achScoring')}</h5>
                <div className="scoring-rules">
                  <div className="scoring-rule">
                    <span className="legend-badge rating-CC">CC</span>
                    <span className="legend-badge rating-C">C</span>
                    <span className="legend-badge rating-N">N</span>
                    <span className="rule-text">= 0 {t('matrix.points')}</span>
                  </div>
                  <div className="scoring-rule">
                    <span className="legend-badge rating-I">I</span>
                    <span className="rule-text">= -1 {t('matrix.point')}</span>
                  </div>
                  <div className="scoring-rule">
                    <span className="legend-badge rating-II">II</span>
                    <span className="rule-text">= -2 {t('matrix.points')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubmissionReview;